/**
 * HelpAboutContent
 *
 * Body of the HelpAboutSheet. Renders a vertical list of tappable rows
 * followed by a centered app-version footer. No title — relies on the
 * sheet's drag indicator at the top.
 *
 * Flutter source: lib/presentation/screens/settings/app_settings_screen.dart
 * (items section + _appVersionWidget).
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { Navigator } from '@/presentation/routing/Navigator';
import { APP_VERSIONING } from '@/config/appVersioning';
import { HelpAboutType } from '@/domain/entities/helpAbout/helpAboutEntity';
import { ListItemTile } from '@/presentation/components/common/ListItemTile';
import { useHelpAboutItems } from '../hooks/useHelpAboutItems';

export const HelpAboutContent: React.FC = () => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  const items = useHelpAboutItems();

  const handleItemClick = (type: HelpAboutType) => {
    switch (type) {
      case HelpAboutType.LEGAL_DOCUMENTS:
        Navigator.navigateToLegalDocuments();
        return;
      case HelpAboutType.CONTACT:
        Navigator.navigateToContact();
        return;
      case HelpAboutType.SUPPORT:
        Navigator.navigateToSupport();
        return;
      case HelpAboutType.ABOUT:
        // Pushes the /about page. Sheets stay open across route pushes per
        // project convention.
        Navigator.navigateToAbout();
        return;
      default:
        return;
    }
  };

  return (
    <div>
      {/* Rows */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          paddingLeft: responsive.spacing.xl,
          paddingRight: responsive.spacing.xl,
          paddingTop: responsive.spacing.lg,
          paddingBottom: responsive.spacing.lg,
        }}
      >
        {items.map((item) => (
          <ListItemTile
            key={item.type}
            title={item.title}
            description={item.description}
            iconPath={item.iconPath}
            onClick={() => handleItemClick(item.type)}
            qa={{ id: `help-about-${item.type}` }}
          />
        ))}
      </div>

      {/* App version footer (centered plain text) */}
      <div
        style={{
          textAlign: 'center',
          color: theme.text.secondary,
          fontSize: responsive.font.sm,
          paddingTop: responsive.spacing.base,
          paddingBottom: responsive.spacing['2xl'],
        }}
      >
        {`${l10n.helpAbout.appVersion} ${APP_VERSIONING.APP_VERSION}`}
      </div>
    </div>
  );
};
